import FadeUp from "../components/FadeUp";
import { COMPANY } from "../lib/constants";

const LAST_UPDATED = "June 2026";

export default function Privacy() {
    return (
        <main
            className="min-h-screen bg-[#050505] text-white pt-28 md:pt-32"
            data-testid="privacy-page"
        >
            <section className="container-bevel section-pad max-w-4xl">
                <FadeUp>
                    <span className="overline">Legal</span>
                    <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[1.05]">
                        Privacy Policy
                    </h1>
                    <p className="mt-6 text-sm uppercase tracking-[0.22em] text-white/50">
                        Last Updated: {LAST_UPDATED}
                    </p>
                </FadeUp>

                <FadeUp delay={1}>
                    <div className="mt-14 space-y-10 text-white/80 leading-relaxed">
                        <section>
                            <p>
                                {COMPANY.name} (&ldquo;Bevel,&rdquo;
                                &ldquo;we,&rdquo; &ldquo;our,&rdquo; or
                                &ldquo;us&rdquo;) respects your privacy. This
                                Privacy Policy explains what information we
                                collect when you visit{" "}
                                <span className="text-white">
                                    bevelcommercial.com
                                </span>{" "}
                                or submit a project inquiry, how we use it, and
                                the choices you have.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl text-white">
                                1. Information We Collect
                            </h2>
                            <p className="mt-4">
                                We only collect information you choose to
                                provide through our contact and project
                                inquiry forms. This may include:
                            </p>
                            <ul className="mt-4 space-y-2 list-disc list-inside text-white/75">
                                <li>Your name</li>
                                <li>Email address</li>
                                <li>Phone number (optional)</li>
                                <li>
                                    Project type and details you share in the
                                    message field
                                </li>
                                <li>
                                    Scope selections you make in the project
                                    checklist
                                </li>
                            </ul>
                            <p className="mt-4">
                                We do not knowingly collect sensitive personal
                                information, financial account numbers, or
                                government-issued identifiers through this
                                website.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl text-white">
                                2. How We Use Your Information
                            </h2>
                            <p className="mt-4">
                                We use the information you submit to:
                            </p>
                            <ul className="mt-4 space-y-2 list-disc list-inside text-white/75">
                                <li>Respond to your inquiry or quote request</li>
                                <li>
                                    Schedule a consultation or walkthrough
                                </li>
                                <li>
                                    Communicate about an active or prospective
                                    project
                                </li>
                                <li>
                                    Maintain internal records of past and
                                    current client communications
                                </li>
                            </ul>
                            <p className="mt-4">
                                We do not sell, rent, or trade your personal
                                information to third parties. We do not use
                                your information for advertising, retargeting,
                                or profiling.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl text-white">
                                3. Service Providers
                            </h2>
                            <p className="mt-4">
                                To deliver and store your inquiry, we rely on
                                a small number of trusted service providers:
                            </p>
                            <ul className="mt-4 space-y-2 list-disc list-inside text-white/75">
                                <li>
                                    <span className="text-white">Resend</span>{" "}
                                    — delivers your form submission to our
                                    team as an email notification.
                                </li>
                                <li>
                                    <span className="text-white">
                                        MongoDB (hosted database)
                                    </span>{" "}
                                    — stores a record of your submission so
                                    we can follow up and keep accurate
                                    project history.
                                </li>
                                <li>
                                    <span className="text-white">Emergent</span>{" "}
                                    — provides the hosting and infrastructure
                                    that runs this website.
                                </li>
                            </ul>
                            <p className="mt-4">
                                These providers are contractually bound to
                                protect your information and may only use it
                                to deliver the service we have engaged them
                                for.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl text-white">
                                4. Cookies, Analytics &amp; Tracking
                            </h2>
                            <p className="mt-4">
                                This website does not use advertising cookies,
                                analytics scripts, pixels, or cross-site
                                trackers. We do not run Google Analytics,
                                Facebook Pixel, LinkedIn Insight Tag, or any
                                comparable tracking tool. The site may use
                                minimal functional storage (such as
                                remembering your admin session) that is strictly
                                necessary for the site to work.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl text-white">
                                5. Data Retention
                            </h2>
                            <p className="mt-4">
                                We retain project inquiries and related
                                communications for as long as needed to
                                maintain our business records and respond to
                                follow-up questions, typically up to seven
                                years for completed projects, consistent with
                                normal construction-industry record-keeping
                                practice. You may request earlier deletion at
                                any time (see Section 7).
                            </p>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl text-white">
                                6. Security
                            </h2>
                            <p className="mt-4">
                                All form submissions are transmitted over an
                                encrypted HTTPS connection. Stored records are
                                protected by access controls on our hosting
                                environment. While no system can be
                                guaranteed to be completely secure, we take
                                reasonable administrative and technical
                                measures to protect the information you share.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl text-white">
                                7. Your Choices &amp; Rights
                            </h2>
                            <p className="mt-4">
                                You may contact us at any time to:
                            </p>
                            <ul className="mt-4 space-y-2 list-disc list-inside text-white/75">
                                <li>
                                    Request a copy of the personal information
                                    we have on file for you
                                </li>
                                <li>
                                    Correct inaccurate information
                                </li>
                                <li>
                                    Request deletion of your information
                                </li>
                                <li>
                                    Opt out of further communications
                                </li>
                            </ul>
                            <p className="mt-4">
                                To exercise any of these rights, email us at{" "}
                                <a
                                    href={COMPANY.emailHref}
                                    className="text-[#F5A623] underline underline-offset-4 hover:text-[#ffb94a]"
                                    data-testid="privacy-contact-email"
                                >
                                    {COMPANY.email}
                                </a>
                                . We will respond within a reasonable period
                                and verify your identity before acting on
                                sensitive requests.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl text-white">
                                8. Children&apos;s Privacy
                            </h2>
                            <p className="mt-4">
                                This site is intended for property owners,
                                operators, and real-estate professionals. We
                                do not knowingly collect information from
                                anyone under the age of 13. If you believe a
                                child has submitted information to us, contact
                                us and we will delete it.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl text-white">
                                9. Changes to This Policy
                            </h2>
                            <p className="mt-4">
                                We may update this Privacy Policy from time to
                                time. The &ldquo;Last Updated&rdquo; date at
                                the top of this page indicates when the policy
                                was most recently revised. Material changes
                                will be highlighted on this page.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl text-white">
                                10. Contact Us
                            </h2>
                            <p className="mt-4">
                                Questions, requests, or concerns about this
                                policy can be sent to:
                            </p>
                            <div className="mt-5 border border-white/10 bg-[#0A0A0A] p-6">
                                <div className="text-white font-display text-lg">
                                    {COMPANY.name}
                                </div>
                                <div className="mt-2 text-white/75 text-sm">
                                    {COMPANY.address}
                                </div>
                                <div className="mt-2 text-sm">
                                    <a
                                        href={COMPANY.emailHref}
                                        className="text-[#F5A623] underline underline-offset-4 hover:text-[#ffb94a]"
                                    >
                                        {COMPANY.email}
                                    </a>
                                </div>
                                <div className="mt-1 text-sm">
                                    <a
                                        href={COMPANY.phoneHref}
                                        className="text-white/80 hover:text-white"
                                    >
                                        {COMPANY.phone}
                                    </a>
                                </div>
                            </div>
                        </section>
                    </div>
                </FadeUp>
            </section>
        </main>
    );
}
